/* eslint-disable import/no-cycle */
import {
  PayloadAction,
  createAction,
  createSlice,
  isAnyOf
} from '@reduxjs/toolkit'
import responseCodes from 'const/responseCodes'
import { ServerError } from 'typings/serverError'
import { LOGGED_OUT, systemErrorPageLoad } from 'redux/actionTypes'
import { ResponseStatus } from 'syf-transmit-mfe/dist/typings/transmit'
import { getResetPasswordEligibilityCheck } from 'redux/resetPassword/async-thunks'
import routePaths from 'const/routePaths'
import { redirectTo } from 'ui/customHooks/useHistory'
import { hasAccountNotSupported } from 'helpers/brandHelpers'
import { userAccountInfo } from 'helpers/user'
import { userCancelledUpdatePassword } from 'redux/resetPassword/resetPasswordSlice'
import { getErrorMessage } from 'helpers/parseError'
import { logout } from 'redux/authToken/authTokenSlice'
import {
  getUserInfo,
  loginSubmitted,
  mfaContactModalSubmitted
} from './async-thunks'
import { AuthenticationState, UserInfoState } from './typings'

export const initState: AuthenticationState = {
  isLoading: false,
  isPasswordUpdated: false,
  isLoginFailure: false,
  isUpdateSuccess: false,
  isUserUpdatePending: false,
  userID: '',
  accountId: '',
  sessionId: '',
  accountIndicator: '',
  firstTimeLogon: '',
  isSwitchLinkedAccountFlow: false,
  userInfo: {
    hasUserInfoResponseSuccess: false,
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    contactUpdateRequired: false
  },
  error: null,
  errorMessage: '',
  jwtToken: ''
}

// Manual Action Creator
export const onLogout = createAction(LOGGED_OUT)

/**
 * Resets the user session values to initial state.
 */
const resetSessionInfo = (state: AuthenticationState) => {
  state.userID = ''
  state.accountIndicator = ''
  state.accountId = ''
  state.sessionId = ''
  state.firstTimeLogon = ''
  state.jwtToken = ''
}

export const authenticationSlice = createSlice({
  name: 'authenticationSlice',
  initialState: initState,
  reducers: {
    updateLoginInfoState: (
      state: AuthenticationState,
      action: PayloadAction<Partial<AuthenticationState>>
    ) => {
      return {
        ...state,
        isLoading: false,
        ...action.payload
      }
    },
    // used only for V3Auth
    checkEligibilityAccountNotSupported: (state: AuthenticationState) => {
      state.isLoginFailure = true
      state.errorMessage = responseCodes.invalidSamsAccountType
      state.isLoading = false
    },
    setUserInfo(
      state: AuthenticationState,
      action: PayloadAction<UserInfoState>
    ) {
      state.isLoading = false
      state.userInfo = action.payload
    },
    resetUpdateStatus(state: AuthenticationState) {
      state.isUpdateSuccess = false
    },
    contactInfoUpdatePending(state: AuthenticationState) {
      state.isUserUpdatePending = true
    },
    contactInfoUpdated(state: AuthenticationState) {
      state.isUpdateSuccess = true
      state.isUserUpdatePending = false
    },
    /**
     * This action resets the error state of login.
     * It is created to fix the re-render issue caused due to React Strict mode for Login component
     */
    clearLoginInfoError(state: AuthenticationState) {
      state.error = null
      state.isLoading = false
      state.isLoginFailure = false
      state.errorMessage = ''
    },
    registrationAccountInfo: (
      state: AuthenticationState,
      action: PayloadAction<Partial<AuthenticationState>>
    ) => {
      state.accountId = action.payload.accountId
      state.accountNumber = action.payload.accountNumber
      state.accountIndicator = action.payload.accountIndicator
    },
    fetchAuthTokenFailed: state => {
      userAccountInfo.accountType = 'generic'
      userAccountInfo.securityLevel = ''
      userAccountInfo.accountIdType = ''
      userAccountInfo.accountID = ''
      userAccountInfo.cardType = ''

      userAccountInfo.accountDetail = {
        'cipher.accountId': '',
        'cipher.partialAccountId': '',
        switchAccountType: 'SELF'
      }

      state.isLoginFailure = true
      state.errorMessage = 'failed to fetch auth token'

      resetSessionInfo(state)
    },
    onLoginTransmitError: (
      state: AuthenticationState,
      action: PayloadAction<ResponseStatus['responseCode']>
    ) => {
      state.errorMessage = action.payload
      resetSessionInfo(state)
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginSubmitted.pending, state => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginSubmitted.fulfilled, (state, { payload, meta }) => {
        state.isLoading = false

        // this conditional block can be removed if/when prox support feature flag is removed
        if (hasAccountNotSupported(payload.accountType)) {
          state.isLoginFailure = true
          state.errorMessage = 'invalid.sams.account.type'
          state.isLoading = false

          return
        }

        const { userID } = meta.arg

        const {
          firstTimeLogon,
          accountType,
          accountIndicator,
          accountIdType,
          ['cipher.accountId']: accountId,
          ['cipher.partialAccountId']: partialAccountId,
          securityLevel,
          sessionId,
          cardType,
          jwtToken
        } = payload

        userAccountInfo.accountType = accountType
        userAccountInfo.securityLevel = securityLevel
        userAccountInfo.accountIdType = accountIdType || ''
        userAccountInfo.accountID = accountId
        userAccountInfo.cardType = cardType

        userAccountInfo.accountDetail = {
          'cipher.accountId': accountId,
          'cipher.partialAccountId': partialAccountId || '',
          switchAccountType: 'SELF'
        }

        state.isLoginFailure = false
        state.errorMessage = ''
        state.isLoading = false
        state.userID = userID
        state.accountId = accountId
        state.sessionId = sessionId
        state.accountIndicator = accountIndicator
        state.firstTimeLogon = firstTimeLogon
        state.jwtToken = jwtToken
      })
      .addCase(loginSubmitted.rejected, (state, action) => {
        const { description } = action.payload

        state.isLoading = false
        state.isLoginFailure = true
        state.error = action.payload
        state.errorMessage = description
        state.userID = ''

        state.errorMessage = getErrorMessage(action.payload)
      })
      .addCase(mfaContactModalSubmitted.pending, state => {
        state.userInfo.contactUpdateRequired = false
      })
      .addCase(getResetPasswordEligibilityCheck.rejected, (state, action) => {
        const error = action.payload as ServerError
        const { businessErrors = [] } = error.response || {}
        const { code: errorCode = '' } = businessErrors[0] || {}

        /**
         * This is sams prox specific logic which was implemented prior to Sams prox implementation to refrain user from logging in
         * with the Sams prox account and display the Account Type not Supported error message
         * Can be removed once the sams prox feature is removed
         */
        if (errorCode === responseCodes.invalidSamsAccountType) {
          state.isLoginFailure = true
          state.errorMessage = responseCodes.invalidSamsAccountType
          state.isLoading = false

          // This is a side effect and should not be here.
          redirectTo(routePaths.home)
          return
        }

        /**
         * If user tries to reset password but the account don't have online access setup, we update
         * loginInfo with an errorMessage to be able to display the AlertBanner explaining that error.
         */
        if (errorCode === responseCodes.accessDenied) {
          state.errorMessage = responseCodes.accessDenied

          // This is a side effect and should not be here.
          redirectTo(routePaths.home)
        }
      })
      .addCase(getUserInfo.pending, state => {
        state.isLoading = true
        state.error = null
      })
      .addCase(systemErrorPageLoad, state => {
        /*
         * Clear the error state whenever the system error page is rendered so
         * that the error will be removed when the user clicks on "go back" to
         * return to the Login page.
         */
        state.isLoginFailure = false
        state.error = null
        state.errorMessage = ''
      })
      .addMatcher(
        isAnyOf(userCancelledUpdatePassword, logout),
        resetSessionInfo
      )
  }
})

export * from './loginInfoSelectors'

export const {
  checkEligibilityAccountNotSupported,
  clearLoginInfoError,
  contactInfoUpdated,
  contactInfoUpdatePending,
  fetchAuthTokenFailed,
  onLoginTransmitError,
  registrationAccountInfo,
  resetUpdateStatus,
  setUserInfo,
  updateLoginInfoState
} = authenticationSlice.actions

export default authenticationSlice.reducer
